import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Link,
    IconButton,
    Divider,
    Button
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useLanguage } from '../contexts/LanguageContext';
import { WHATSAPP_URL } from '../config';
import { translations } from '../translations/translations';
import { Link as RouterLink } from 'react-router-dom';
import { useConsent } from '../consent/ConsentContext';
import { useNewsletter } from '../newsletter/NewsletterContext';

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const { language, isHebrew } = useLanguage();
    const t = translations[language]; // Use current language translations
    const { openPreferences } = useConsent();
    const { openNewsletterModal, openUnsubscribeModal } = useNewsletter();

    // Footer link labels
    const termsText = isHebrew ? 'תנאי שימוש' : t.termsOfService;
    const siteTermsText = isHebrew ? 'תקנון האתר' : t.siteTerms;
    const privacyText = isHebrew ? 'מדיניות פרטיות' : t.privacyPolicy;
    const returnsText = isHebrew ? 'החזרות' : t.returns;

    return (
        <Box
            id="site-footer"
            sx={{
                backgroundColor: 'rgba(229, 90, 61, 1)',
                borderTop: '1px solid #e0e0e0',
                py: 4,
                mt: 'auto',
                direction: isHebrew ? 'rtl' : 'ltr',
                textAlign: isHebrew ? 'right' : 'left'
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ position: 'relative' }}>

                    {/* Company Information */}
                    <Grid item xs={12} md={6} sx={isHebrew ? { ml: 5 } : {}}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: 'rgb(245, 240, 227)',
                                mb: 0.5
                            }}
                        >
                            {t.storeName}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                lineHeight: 1.6,
                                mb: 1
                            }}
                        >
                            {t.storeDescription}
                        </Typography>
                        <Button
                            onClick={openNewsletterModal}
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                px: 0,
                                mb: 1,
                                justifyContent: 'flex-start',
                                textDecoration: 'underline',
                                textUnderlineOffset: '3px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#fff'
                                }
                            }}
                        >
                            {t.newsletterFooterCta}
                        </Button>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, alignItems: isHebrew ? 'flex-start' : 'flex-start' }}>
                            <Button
                                component="a"
                                href="https://mail.google.com/mail/?view=cm&fs=1&to=hamikdash.today@gmail.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<EmailIcon />}
                                sx={{
                                    color: 'rgb(245, 240, 227)',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    width: 'auto',
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent',
                                    justifyContent: 'flex-start',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: isHebrew ? '8px' : '0px',
                                        marginRight: isHebrew ? '0px' : '8px'
                                    },

                                    '&:hover': {
                                        border: '1px solid rgb(245, 240, 227)',
                                        color: 'rgb(245, 240, 227)'
                                    }
                                }}
                            >
                                hamikdash.today@gmail.com                            </Button>
                            <Button
                                component="a"
                                href="tel:+972532405276"
                                startIcon={<PhoneIcon />}
                                sx={{
                                    color: 'rgb(245, 240, 227)',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    width: 'auto',
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent',
                                    justifyContent: 'flex-start',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: isHebrew ? '8px' : '0px',
                                        marginRight: isHebrew ? '0px' : '8px'
                                    },
                                    '&:hover': {
                                        border: '1px solid rgb(245, 240, 227)',
                                        color: 'rgb(245, 240, 227)'
                                    }
                                }}
                            >
                                053-2405276
                            </Button>
                            <Button
                                component="a"
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<WhatsAppIcon />}
                                sx={{
                                    color: 'rgb(245, 240, 227)',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    width: 'auto',
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent',
                                    justifyContent: 'flex-start',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: isHebrew ? '8px' : '0px',
                                        marginRight: isHebrew ? '0px' : '8px'
                                    },
                                    '&:hover': {
                                        border: '1px solid #25D366',
                                        color: '#25D366'
                                    }
                                }}
                            >
                                {isHebrew ? 'WhatsApp' : 'WhatsApp'}
                            </Button>
                            <Button
                                component="a"
                                href="https://maps.google.com/?q=Aliya+7+Netivot+Israel"
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<LocationOnIcon />}
                                sx={{
                                    color: 'rgb(245, 240, 227)',
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    width: 'auto',
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent',
                                    justifyContent: 'flex-start',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: isHebrew ? '8px' : '0px',
                                        marginRight: isHebrew ? '0px' : '8px'
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(245, 240, 227, 0.1)',
                                        border: '1px solid rgb(245, 240, 227)',
                                        color: 'rgb(245, 240, 227)'
                                    }
                                }}
                            >
                                עליה 7 נתיבות                            </Button>
                        </Box>
                    </Grid>



                    {/* Social Media */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            display: 'flex',
                            ...(isHebrew ? {
                                borderRight: { md: '1px solid rgba(245, 240, 227, 0.3)', xs: 'none' },
                                pr: { xs: 0, md: 5 }
                            } : {
                                borderLeft: { md: '1px solid rgba(245, 240, 227, 0.3)', xs: 'none' },
                                pl: { xs: 0, md: 5 }
                            }),
                            flexDirection: 'column',
                            alignItems: isHebrew ? 'flex-end' : 'flex-start'
                        }}>

                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'rgb(245, 240, 227)',
                                    mb: 0.5,
                                    textAlign: isHebrew ? 'right' : 'left',

                                }}
                            >
                                {t.followUs}
                            </Typography>

                            {/* Social Media Icons */}
                            <Box sx={{ display: 'flex', gap: 0 }}>
                                <IconButton
                                    component="a"
                                    href="https://www.facebook.com/share/1CGo7JT8ka/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disableRipple
                                    sx={{
                                        color: 'rgb(245, 240, 227)',
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            color: '#1877f2',
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    <FacebookIcon />
                                </IconButton>
                                <IconButton
                                    component="a"
                                    href="https://www.instagram.com/b_mikdash/profilecard/?igsh=enMwZ21yZ3h4MGpt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disableRipple
                                    sx={{
                                        color: 'rgb(245, 240, 227)',
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            color: '#e4405f',
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Divider */}
                <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

                {/* Bottom Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2
                    }}
                >
                    {/* Copyright */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgb(245, 240, 227)',
                            textAlign: { xs: 'center', md: isHebrew ? 'right' : 'left' }
                        }}
                    >
                        © {currentYear} {t.allRightsReserved}
                    </Typography>

                    {/* Legal Links */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            // Tighter vertical gap when Cookie Preferences wraps on mobile
                            columnGap: { xs: 2, md: 3 },
                            rowGap: { xs: 0.75, md: 1.5 },
                            justifyContent: { xs: 'center', md: isHebrew ? 'flex-start' : 'flex-end' },
                            alignItems: 'baseline'
                        }}
                    >
                        <Link
                            component="button"
                            type="button"
                            underline="none"
                            onClick={openUnsubscribeModal}
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                fontFamily: 'inherit',
                                lineHeight: 'inherit',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                verticalAlign: 'baseline',
                                '&:hover': {
                                    color: '#222222',
                                    fontWeight: 400,
                                    backgroundColor: 'transparent',
                                    textDecoration: 'none'
                                }
                            }}
                        >
                            {t.newsletterFooterUnsubscribe}
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/terms"
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: '#222222' }
                            }}
                        >
                            {termsText}
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/site-terms"
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: '#222222' }
                            }}
                        >
                            {siteTermsText}
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/privacy"
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: '#222222' }
                            }}
                        >
                            {privacyText}
                        </Link>
                        <Link
                            component={RouterLink}
                            to="/returns"
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: '#222222' }
                            }}
                        >
                            {returnsText}
                        </Link>
                        <Link
                            component="button"
                            type="button"
                            underline="none"
                            onClick={openPreferences}
                            sx={{
                                color: 'rgb(245, 240, 227)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                fontFamily: 'inherit',
                                lineHeight: 'inherit',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                verticalAlign: 'baseline',
                                '&:hover': {
                                    color: '#222222',
                                    fontWeight: 400,
                                    backgroundColor: 'transparent',
                                    textDecoration: 'none'
                                }
                            }}
                        >
                            {t.cookiePreferences}
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box >
    );
} 